import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Check, ChevronRight, Clapperboard, Crosshair, FileUp, Link2, OctagonAlert, Sparkles, Star, Swords, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { AIBadge, EmptyState, ErrorState, FormStrip, LoadMore, MapBanner, MapChip, PageSkeleton, StatusBadge } from "@/components/cs";
import { FaceitLoginButton, SteamLoginButton } from "@/components/layout";
import { FaceitCard } from "@/components/faceit";
import { TagList, TagToggles } from "@/components/tags";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEntitlements, useMe } from "@/hooks/use-me";
import { useTags } from "@/hooks/use-tags";
import { api, type FeedFilter, type FeedMatch, type FeedPage, type FeedPending, type FeedSort, type FeedSummary, type SteamLink, type Tag, type WeekInsights } from "@/lib/api";
import { MISTAKE } from "@/lib/mistakes";
import { ago, bytes, CATEGORY, categoryLabel, date, KIND, mapName } from "@/lib/format";
import { cn } from "@/lib/utils";

const DEMO_EXTENSIONS = [".dem", ".dem.gz", ".dem.bz2", ".dem.zst"];

type VideoKind = "highlights" | "kills" | "mistakes";
const VIDEO_OPTIONS = [
  { kind: "highlights", icon: Star },
  { kind: "kills", icon: Crosshair },
  { kind: "mistakes", icon: OctagonAlert },
] as const;

export function HomePage() {
  const { data: me, isLoading } = useMe();
  if (isLoading) return <PageSkeleton />;
  return me ? <Dashboard /> : <Landing />;
}

// ---- Logged out ----

const FEATURES = [
  { icon: BarChart3, title: "Полная статистика", text: "K/D, ADR, KAST, открывающие дуэли, клатчи 1v1–1v5, утилита и смерти без размена." },
  { icon: Swords, title: "Дуэли и раунды", text: "Кто кого убивал, кто выигрывал клатчи, как шёл матч по раундам." },
  { icon: Sparkles, title: "ИИ-разбор ошибок", text: "Модель разбирает факты из демки и показывает повторяющиеся ошибки с номерами раундов.", ai: true },
  { icon: Clapperboard, title: "Видео из демки", text: "Хайлайты, все фраги или ваши ошибки — отрендеренные прямо из игры, с подписями." },
];

const STEPS = [
  { n: "01", title: "Загрузите демку", text: "Перетащите .dem или включите автоимпорт Premier из Steam. FACEIT — через вход с FACEIT." },
  { n: "02", title: "Получите разбор", text: "Через минуту — статистика всего матча, дуэли, клатчи и список ваших ошибок по раундам." },
  { n: "03", title: "Смотрите и тренируйте", text: "Видео ваших фрагов и ошибок прямо из игры, ИИ-советы и тренды по категориям." },
];

function Landing() {
  return (
    <div className="space-y-20 pb-8 pt-6 sm:pt-12">
      <section className="grid items-center gap-10 lg:grid-cols-2">
        <div className="animate-in-up text-center lg:text-left">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border bg-card/60 px-3 py-1 text-xs text-muted-foreground">
            <span className="size-1.5 rounded-full bg-win" /> Premier, Matchmaking и FACEIT
          </div>
          <h1 className="text-4xl font-extrabold leading-[1.05] sm:text-5xl">
            Разбор демок <span className="text-primary glow-primary">CS2</span>
            <br />
            <span className="text-ai">с ИИ-тренером</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-muted-foreground sm:text-lg lg:mx-0">
            Загрузите демку — получите статистику всего матча, дуэли, клатчи, разбор ваших ошибок и видео лучших моментов.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3 lg:justify-start">
            <SteamLoginButton size="lg" />
            <FaceitLoginButton size="lg" />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Входя, вы принимаете <Link to="/terms" className="underline hover:text-foreground">условия</Link> и{" "}
            <Link to="/privacy" className="underline hover:text-foreground">политику конфиденциальности</Link>.
          </p>
        </div>
        <Preview />
      </section>

      <section className="stagger grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURES.map(({ icon: Icon, title, text, ai }) => (
          <Card key={title} className={cn("lift gap-3", ai && "ai-frame")}>
            <CardHeader>
              <span className={cn("grid size-10 place-items-center rounded-lg", ai ? "bg-ai-from/15" : "bg-primary/15")}>
                <Icon className={cn("size-5", ai ? "text-ai-to" : "text-primary")} />
              </span>
              <CardTitle className={cn("font-display", ai && "text-ai")}>{title}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">{text}</CardContent>
          </Card>
        ))}
      </section>

      <section>
        <h2 className="mb-6 text-center text-2xl font-bold sm:text-3xl">Как это работает</h2>
        <div className="grid gap-3 md:grid-cols-3">
          {STEPS.map((st) => (
            <div key={st.n} className="relative overflow-hidden rounded-xl border bg-card p-5">
              <span className="pointer-events-none absolute -right-2 -top-4 font-display text-7xl font-extrabold text-primary/10">{st.n}</span>
              <div className="font-display text-sm font-bold text-primary">{st.n}</div>
              <div className="mt-2 font-display text-lg font-semibold">{st.title}</div>
              <p className="mt-1 text-sm text-muted-foreground">{st.text}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/** A still of the product built from the real components. */
function Preview() {
  const rows = [
    { name: "you", k: "27/14/6", adr: 112, w: 100, me: true },
    { name: "tvoy_batya", k: "20/17/5", adr: 91, w: 81 },
    { name: "kechkishamal", k: "16/16/3", adr: 68, w: 61 },
  ];
  return (
    <div className="relative mx-auto w-full max-w-md animate-in-up lg:max-w-none">
      <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 size-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full border border-primary/15">
        <div className="radar-sweep absolute inset-0 rounded-full" />
        <div className="absolute inset-[18%] rounded-full border border-primary/10" />
        <div className="absolute inset-[36%] rounded-full border border-primary/10" />
      </div>
      <div className="overflow-hidden rounded-2xl border bg-card shadow-2xl shadow-black/40">
        <MapBanner map="de_mirage" className="h-32" watermark={false}>
          <div className="flex h-full flex-col justify-between p-4">
            <div className="flex justify-between text-xs">
              <span className="glass rounded-md px-2 py-0.5">Premier</span>
              <span className="glass rounded-md px-2 py-0.5 text-muted-foreground">сегодня</span>
            </div>
            <div className="flex items-end justify-between">
              <span className="flex items-center gap-2">
                <MapChip map="de_mirage" className="size-14" />
                <span className="font-display text-3xl font-extrabold uppercase">Mirage</span>
              </span>
              <span className="num font-display text-4xl font-extrabold text-win glow-win">13<span className="text-foreground/50">:</span>9</span>
            </div>
          </div>
        </MapBanner>
        <div className="divide-y">
          {rows.map((r) => (
            <div key={r.name} className={cn("flex items-center gap-3 px-4 py-2.5", r.me && "bg-primary/5")}>
              <span className="w-28 truncate text-sm font-medium">{r.name}{r.me && <span className="ml-1.5 rounded bg-primary/15 px-1 font-display text-[10px] font-bold text-primary">MVP</span>}</span>
              <span className="num w-20 font-display text-sm font-bold">{r.k}</span>
              <span className="flex-1">
                <span className="num text-xs text-muted-foreground">ADR {r.adr}</span>
                <span className="mt-1 block h-1 rounded-full bg-secondary"><span className="block h-full rounded-full bg-ct" style={{ width: `${r.w}%` }} /></span>
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* On a phone the card sits under the preview: floating, it covered two of its three rows. */}
      <div className="ai-frame mt-3 rounded-xl p-3 shadow-xl shadow-black/40 sm:absolute sm:-bottom-8 sm:-right-6 sm:mt-0 sm:w-64">
        <AIBadge />
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground">Открывающие смерти без размена</span> в р. 4, 9, 17 — вы заходите первым без флешки от команды.
        </p>
      </div>
    </div>
  );
}

// ---- Logged in ----

function Dashboard() {
  const { data: me } = useMe();
  const [filter, setFilter] = useState<FeedFilter>({ category: null, map: null, result: null, tag: null, sort: "date" });
  const set = (patch: Partial<FeedFilter>) => setFilter((f) => ({ ...f, ...patch }));
  // The unfiltered first page holds what is being parsed, the categories and the hero numbers.
  const head = useQuery({
    queryKey: ["feed", "head"],
    queryFn: () => api<FeedPage>("/api/feed"),
    // Keep polling while something is being parsed.
    refetchInterval: (q) => (q.state.data?.pending?.some((d) => d.status === "uploaded" || d.status === "parsing") ? 3000 : false),
  });
  const feed = useInfiniteQuery({
    queryKey: ["feed", "list", filter],
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams();
      if (filter.category) q.set("category", filter.category);
      if (filter.map) q.set("map", filter.map);
      if (filter.result) q.set("result", filter.result);
      if (filter.tag) q.set("tag", String(filter.tag));
      if (filter.sort !== "date") q.set("sort", filter.sort);
      // The default sort pages by cursor, the others by offset.
      if (pageParam) q.set(filter.sort === "date" ? "before" : "offset", pageParam);
      return api<FeedPage>(`/api/feed?${q}`);
    },
    initialPageParam: "",
    getNextPageParam: (last) => last.next ?? undefined,
  });
  // A parse that finishes adds a match: reload the list with it.
  const qc = useQueryClient();
  const parsing = head.data?.pending?.length ?? 0;
  const prevParsing = useRef(parsing);
  useEffect(() => {
    if (parsing < prevParsing.current) void qc.invalidateQueries({ queryKey: ["feed", "list"] });
    prevParsing.current = parsing;
  }, [parsing, qc]);

  const matches = feed.data?.pages.flatMap((p) => p.matches) ?? [];
  const pending = head.data?.pending ?? [];
  const categories = head.data?.categories ?? [];
  const maps = head.data?.maps ?? [];
  const { data: tags } = useTags();
  const filtered = !!(filter.map || filter.result || filter.tag);
  // Counts exist per category only; under a map/result/tag filter the total is unknown, so none is shown.
  const total = filtered ? 0 : filter.category ? categories.find((c) => c.category === filter.category)?.n ?? 0 : categories.reduce((a, c) => a + c.n, 0);

  return (
    <div className="space-y-6 animate-in-up">
      <SummaryHero name={me?.display_name ?? ""} steamId={me?.steam_id ?? ""} summary={head.data?.summary} />
      {head.data?.week && <WeekStrip week={head.data.week} />}
      {/* Phone: upload, matches, imports. Desktop: matches on the left, the rest on the right. */}
      {/* Row 1 fits the upload card, row 2 takes the rest, so the import cards sit right under it. */}
      <div className="grid gap-6 lg:grid-cols-3 lg:grid-rows-[auto_1fr] lg:items-start [&>*]:min-w-0">
        <div className="lg:col-start-3 lg:row-start-1">
          <UploadCard />
        </div>
        <section className="space-y-3 lg:col-span-2 lg:col-start-1 lg:row-span-2 lg:row-start-1">
          <div className="flex items-baseline justify-between">
            <h2 className="text-xl font-bold">Матчи</h2>
            {total > 0 && <span className="num text-sm text-muted-foreground">{total}</span>}
          </div>
          {categories.length > 1 && (
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 scrollbar-none">
              {[null, ...categories.map((c) => c.category)].map((c) => (
                <button key={c ?? "all"} type="button" onClick={() => set({ category: c })}
                  className={cn("shrink-0 rounded-full border px-3 py-1 text-sm transition-colors",
                    filter.category === c ? "border-primary bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground")}>
                  {c ? CATEGORY[c] ?? c : "Все"}
                </button>
              ))}
            </div>
          )}
          <FeedControls filter={filter} set={set} maps={maps} tags={tags} filtered={filtered} />
          {/* A failed load is not "no matches"; a failed next page keeps the list. */}
          {(feed.error || head.error) && !matches.length ? (
            <ErrorState error={feed.error ?? head.error} />
          ) : feed.isLoading || head.isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 4 }, (_, i) => <div key={i} className="h-40 animate-pulse rounded-xl bg-muted" />)}
            </div>
          ) : !matches.length && !pending.length ? (
            <EmptyState icon={FileUp} title="Матчей пока нет">
              Перетащите .dem из папки CS2 (game/csgo/…) прямо на страницу, включите автоимпорт из Steam или привяжите FACEIT — матчи подтянутся сами.
            </EmptyState>
          ) : (
            <>
              {/* Demos still parsing belong to the unfiltered list only. */}
              <MatchGrid matches={matches} pending={filter.category || filtered ? [] : pending} />
              {feed.hasNextPage && (
                <LoadMore loading={feed.isFetchingNextPage} shown={matches.length} total={total} onClick={() => feed.fetchNextPage()} />
              )}
            </>
          )}
        </section>
        <aside className="space-y-4 lg:col-start-3 lg:row-start-2">
          <SteamImportCard />
          <FaceitCard />
        </aside>
      </div>
    </div>
  );
}

/** What changed this week: each number next to the week before, plus the most frequent mistake. */
function WeekStrip({ week }: { week: WeekInsights }) {
  const delta = (now: number, before: number | null, suffix = "", good: "up" | "down" = "up") => {
    if (before === null) return null;
    const d = Math.round((now - before) * 100) / 100;
    if (!d) return <span className="text-muted-foreground">без изменений</span>;
    const better = good === "up" ? d > 0 : d < 0;
    return <span className={better ? "text-win" : "text-loss"}>{d > 0 ? "+" : ""}{d}{suffix} к прошлой</span>;
  };
  const cells: { label: string; value: string; note: ReactNode; tone?: string }[] = [
    { label: "Винрейт", value: `${week.winRate}%`, note: delta(week.winRate, week.winRateBefore, " п.п.") },
    { label: "K/D", value: week.kd.toFixed(2), note: delta(week.kd, week.kdBefore) },
    { label: "ADR", value: String(week.adr), note: delta(week.adr, week.adrBefore) },
  ];
  if (week.rankChange) {
    cells.push({ label: "Рейтинг", value: `${week.rankChange > 0 ? "+" : ""}${week.rankChange}`, tone: week.rankChange > 0 ? "text-win" : "text-loss", note: <span className="text-muted-foreground">за неделю</span> });
  }
  return (
    <section className="rounded-2xl border bg-card/60 p-4 sm:p-5">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="font-display text-base font-bold">Эта неделя</h2>
        <span className="num text-xs text-muted-foreground">{week.matches} {plural(week.matches, "матч", "матча", "матчей")}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cells.map((c) => (
          <div key={c.label} className="rounded-xl bg-background/50 px-3 py-2.5">
            <div className={cn("num font-display text-xl font-bold", c.tone)}>{c.value}</div>
            <div className="text-[11px] text-muted-foreground">{c.label}</div>
            <div className="num mt-0.5 text-[11px]">{c.note}</div>
          </div>
        ))}
        {week.topMistake && (
          <div className="col-span-2 rounded-xl border border-loss/30 bg-loss/5 px-3 py-2.5 sm:col-span-1">
            <div className="text-[11px] text-muted-foreground">Чаще всего</div>
            <div className="text-sm font-semibold">{MISTAKE[week.topMistake.type]?.title ?? week.topMistake.type}</div>
            <div className="num text-[11px] text-muted-foreground">{week.topMistake.n} {plural(week.topMistake.n, "раз", "раза", "раз")} за неделю</div>
          </div>
        )}
      </div>
    </section>
  );
}

/** Greeting and the player's form over all their matches. */
function SummaryHero({ name, steamId, summary }: { name: string; steamId: string; summary?: FeedSummary }) {
  const n = summary?.matches ?? 0;
  const { wins = 0, kills = 0, deaths = 0, adr = 0, form = [] } = summary ?? {};
  const stats = [
    { label: "Матчей", value: String(n) },
    { label: "Винрейт", value: n ? `${Math.round((100 * wins) / n)}%` : "—", tone: wins * 2 >= n ? "text-win" : "text-loss" },
    { label: "K/D", value: deaths ? (kills / deaths).toFixed(2) : "—", tone: kills >= deaths ? "text-win" : "text-loss" },
    { label: "ADR", value: n ? adr.toFixed(0) : "—" },
  ];
  return (
    <div className="relative overflow-hidden rounded-2xl border bg-card">
      <div className="pointer-events-none absolute -left-20 -top-24 size-72 rounded-full bg-primary/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 right-10 size-64 rounded-full bg-ct/10 blur-3xl" />
      <div className="relative flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">С возвращением,</p>
          <h1 className="truncate text-2xl font-bold sm:text-3xl">{name}</h1>
          {form.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>Форма</span>
              <FormStrip results={[...form].reverse()} />
            </div>
          )}
        </div>
        <div className="grid grid-cols-4 gap-2 sm:gap-3">
          {stats.map((st) => (
            <div key={st.label} className="rounded-xl border bg-background/40 px-3 py-2.5 text-center sm:min-w-24">
              <div className={cn("num font-display text-xl font-bold sm:text-2xl", n && st.tone)}>{st.value}</div>
              <div className="text-[11px] text-muted-foreground">{st.label}</div>
            </div>
          ))}
        </div>
      </div>
      {steamId && n > 0 && (
        <Link to={`/player/${steamId}`} className="relative flex items-center justify-between border-t px-5 py-2.5 text-sm text-muted-foreground hover:text-foreground sm:px-6">
          Подробная статистика, карты и тренды <ChevronRight className="size-4" />
        </Link>
      )}
    </div>
  );
}

type Result = "win" | "loss" | "draw";
const resultOf = (m: FeedMatch): Result | null =>
  !m.my_team ? null : !m.winner_team ? "draw" : m.winner_team === m.my_team ? "win" : "loss";

/** Sorting and the narrower filters: map, result, tag. */
function FeedControls({ filter, set, maps, tags, filtered }: {
  filter: FeedFilter; set: (p: Partial<FeedFilter>) => void;
  maps: { map: string; n: number }[]; tags?: Tag[]; filtered: boolean;
}) {
  const SORTS: [FeedSort, string][] = [
    ["date", "Сначала новые"], ["oldest", "Сначала старые"], ["rating", "По рейтингу матча"],
    ["kd", "По K/D"], ["adr", "По ADR"], ["change", "По изменению рейтинга"],
  ];
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Select value={filter.sort} onValueChange={(v) => set({ sort: v as FeedSort })}>
        <SelectTrigger size="sm" className="w-auto min-w-44"><SelectValue /></SelectTrigger>
        <SelectContent>{SORTS.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}</SelectContent>
      </Select>
      {maps.length > 1 && (
        <Select value={filter.map ?? "all"} onValueChange={(v) => set({ map: v === "all" ? null : v })}>
          <SelectTrigger size="sm" className="w-auto min-w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все карты</SelectItem>
            {maps.map((m) => <SelectItem key={m.map} value={m.map}>{mapName(m.map)} · {m.n}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {(["win", "loss"] as const).map((r) => (
        <button key={r} type="button" onClick={() => set({ result: filter.result === r ? null : r })}
          className={cn("rounded-full border px-3 py-1 transition-colors",
            filter.result === r ? (r === "win" ? "border-win bg-win/15 text-win" : "border-loss bg-loss/15 text-loss") : "text-muted-foreground hover:text-foreground")}>
          {r === "win" ? "Победы" : "Поражения"}
        </button>
      ))}
      {(tags ?? []).length > 0 && (
        <Select value={filter.tag ? String(filter.tag) : "all"} onValueChange={(v) => set({ tag: v === "all" ? null : Number(v) })}>
          <SelectTrigger size="sm" className="w-auto min-w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все теги</SelectItem>
            {(tags ?? []).map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {filtered && (
        <button type="button" className="text-muted-foreground underline-offset-4 hover:underline"
          onClick={() => set({ map: null, result: null, tag: null })}>
          Сбросить
        </button>
      )}
    </div>
  );
}

function MatchGrid({ matches, pending }: { matches: FeedMatch[]; pending: FeedPending[] }) {
  const { data: tags } = useTags();
  return (
    <div className="stagger grid gap-3 sm:grid-cols-2 [&>*]:min-w-0">
      {pending.map((d) => <PendingCard key={`d${d.id}`} d={d} />)}
      {matches.map((m) => <MatchCard key={m.match_id} m={m} tags={tags} />)}
    </div>
  );
}

function MatchCard({ m, tags }: { m: FeedMatch; tags?: Tag[] }) {
  const r = resultOf(m);
  // Scores from the player's side when they played.
  const [us, them] = m.my_team === "B" ? [m.score_b, m.score_a] : [m.score_a, m.score_b];
  const summary = m.detail === "summary";
  return (
    <Link to={`/match/${m.match_id}`} className="lift block overflow-hidden rounded-xl border bg-card">
      <MapBanner map={m.map} className="h-28" watermark={false}>
        <div className="flex h-full flex-col justify-between p-3.5">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="glass rounded-md px-2 py-0.5 font-medium">{categoryLabel(m.category, m.server_name)}</span>
            <span className="flex items-center gap-1.5">
              {m.avg_rank ? (
                <span className="glass num rounded-md px-2 py-0.5 text-muted-foreground" title="Средний рейтинг матча">
                  ~{Math.round(m.avg_rank).toLocaleString("ru")}
                </span>
              ) : null}
              <span className="glass num rounded-md px-2 py-0.5 text-muted-foreground">{date(m.played_at ?? m.created_at)}</span>
            </span>
          </div>
          <div className="flex items-end justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2">
              <MapChip map={m.map} className="size-12" />
              <span className="truncate font-display text-2xl font-extrabold uppercase tracking-wide drop-shadow">{mapName(m.map)}</span>
            </span>
            <span className={cn("num font-display text-3xl font-extrabold drop-shadow", r === "win" && "text-win glow-win", r === "loss" && "text-loss")}>
              {us}<span className="mx-0.5 text-foreground/50">:</span>{them}
            </span>
          </div>
        </div>
      </MapBanner>
      <div className="flex items-center gap-3 px-3.5 py-2.5">
        {r && <span className={cn("h-8 w-1 shrink-0 rounded-full", r === "win" ? "bg-win" : r === "loss" ? "bg-loss" : "bg-muted")} />}
        {m.my_kills !== null ? (
          <div className="num flex flex-1 items-baseline gap-4 text-sm">
            <span className="font-display text-base font-bold">{m.my_kills}<span className="text-muted-foreground">/</span>{m.my_deaths}<span className="text-muted-foreground">/</span>{m.my_assists}</span>
            <span className="text-muted-foreground">ADR <b className="text-foreground">{Math.round(m.my_adr ?? 0)}</b></span>
            {!summary && <span className="hidden text-muted-foreground min-[420px]:inline">KAST <b className="text-foreground">{Math.round(m.my_kast ?? 0)}%</b></span>}
            {m.my_rank_change ? (
              <span className={cn("ml-auto font-semibold", m.my_rank_change > 0 ? "text-win" : "text-loss")}
                title={m.my_rank ? `Рейтинг до матча: ${m.my_rank.toLocaleString("ru")}` : undefined}>
                {m.my_rank_change > 0 ? "+" : ""}{m.my_rank_change}
              </span>
            ) : null}
          </div>
        ) : (
          <span className="flex-1 text-sm text-muted-foreground">Вы не играли в этом матче</span>
        )}
        {summary && <span className="shrink-0 rounded-md border px-1.5 py-0.5 text-[11px] text-muted-foreground" title="Без демки: только статистика платформы">только статистика</span>}
      </div>
      {m.tag_ids.length > 0 && <div className="px-3.5 pb-3"><TagList ids={m.tag_ids} tags={tags} /></div>}
    </Link>
  );
}

/** A demo that is not a match yet: uploading, parsing or failed. */
function PendingCard({ d }: { d: FeedPending }) {
  const busy = d.status === "uploaded" || d.status === "parsing";
  return (
    <div className="flex items-center gap-3 overflow-hidden rounded-xl border border-dashed bg-card/60 p-3.5">
      <span className={cn("grid size-11 shrink-0 place-items-center rounded-lg bg-secondary", busy && "animate-pulse")}>
        <FileUp className="size-5 text-muted-foreground" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium" title={d.file_name}>{d.file_name}</div>
        <div className={cn("truncate text-xs", d.error ? "text-loss" : "text-muted-foreground")}>
          {d.error ?? (busy ? "Разбираем демку — статистика появится через минуту" : ago(d.created_at))}
        </div>
      </div>
      <StatusBadge status={d.status} />
    </div>
  );
}

// PUT with upload progress; fetch cannot report it.
function put(url: string, body: Blob, onProgress: (loaded: number) => void) {
  return new Promise<{ etag?: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 300) return reject(new Error(`Загрузка: ${xhr.status}`));
      try { resolve(xhr.responseText ? JSON.parse(xhr.responseText) : {}); } catch { resolve({}); }
    };
    xhr.onerror = () => reject(new Error("Загрузка прервалась"));
    xhr.send(body);
  });
}

// Big demos go through the Worker in parts (its request limit is 100 MB);
// each part is retried, so a flaky mobile connection does not restart the file.
async function uploadInParts(demoId: number, f: File, partSize: number, setProgress: (p: number) => void) {
  const base = `/api/demos/${demoId}/multipart`;
  const { uploadId } = await api<{ uploadId: string }>(base, { method: "POST" });
  const parts: { partNumber: number; etag: string }[] = [];
  for (let n = 1, offset = 0; offset < f.size; n++, offset += partSize) {
    const chunk = f.slice(offset, offset + partSize);
    for (let attempt = 1; ; attempt++) {
      try {
        const { etag } = await put(`${base}/${n}?uploadId=${encodeURIComponent(uploadId)}`, chunk, (loaded) => setProgress((offset + loaded) / f.size));
        if (!etag) throw new Error("Загрузка: нет etag");
        parts.push({ partNumber: n, etag });
        break;
      } catch (e) {
        if (attempt >= 3) throw e;
        await new Promise((r) => setTimeout(r, 1000 * attempt));
      }
    }
  }
  await api(`${base}/complete`, { json: { uploadId, parts } });
}

type QueueItem = { id: string; file: File; status: "waiting" | "uploading" | "done" | "error"; progress: number; error?: string };

const plural = (n: number, one: string, few: string, many: string) =>
  n % 10 === 1 && n % 100 !== 11 ? one : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? few : many;

/**
 * A cheap fingerprint of the file: the first and last megabytes plus its size.
 * Enough to recognise the same demo a teammate already uploaded, without
 * reading hundreds of megabytes on a phone.
 */
async function fingerprint(f: File) {
  const edge = 1024 * 1024;
  const parts = [f.slice(0, edge), f.slice(Math.max(f.size - edge, 0))];
  const buf = new Uint8Array(await new Blob([...parts, String(f.size)]).arrayBuffer());
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

type UploadResult = { duplicate?: boolean; videos?: { kind: string; error?: string }[] };

async function uploadDemo(f: File, videos: VideoKind[], tagIds: number[], onProgress: (p: number) => void): Promise<UploadResult> {
  let print: string | undefined;
  try {
    print = await fingerprint(f);
  } catch { /* no crypto.subtle over plain http: upload as before */ }
  const start = await api<{ demoId: number; uploadUrl: string; partSize?: number; duplicate?: boolean; matchId?: number }>("/api/demos", {
    json: { fileName: f.name, sizeBytes: f.size, lastModified: f.lastModified, tagIds, fingerprint: print },
  });
  // Already on the site: the match is linked, nothing to upload.
  if (start.duplicate) {
    onProgress(1);
    return { duplicate: true };
  }
  const { demoId, uploadUrl, partSize } = start;
  if (partSize) await uploadInParts(demoId, f, partSize, onProgress);
  else await put(uploadUrl, f, (loaded) => onProgress(loaded / f.size));
  return api<{ videos: { kind: string; error?: string }[] }>(`/api/demos/${demoId}/uploaded`, { json: { videos } });
}

function UploadCard({ className }: { className?: string }) {
  const qc = useQueryClient();
  const ent = useEntitlements();
  const input = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [videos, setVideos] = useState<VideoKind[]>([]);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [pageDrag, setPageDrag] = useState(false);

  // Demos can be dropped anywhere on the page, not only on the drop zone.
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes("Files");
    const enter = (e: DragEvent) => { if (hasFiles(e)) { depth++; setPageDrag(true); } };
    const leave = (e: DragEvent) => { if (hasFiles(e) && --depth <= 0) { depth = 0; setPageDrag(false); } };
    const over = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setPageDrag(false);
      pickRef.current(e.dataTransfer?.files);
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragleave", leave);
    window.addEventListener("dragover", over);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("dragover", over);
      window.removeEventListener("drop", drop);
    };
  }, []);

  const patch = (id: string, p: Partial<QueueItem>) => setItems((all) => all.map((it) => (it.id === id ? { ...it, ...p } : it)));
  const pending = items.filter((it) => it.status === "waiting" || it.status === "error");

  // One file at a time: parallel uploads only split the same bandwidth and
  // make a flaky phone connection fail more often.
  const run = async () => {
    setBusy(true);
    let ok = 0;
    let known = 0; // already on the site, nothing was uploaded
    const warnings = new Set<string>();
    for (const it of pending) {
      patch(it.id, { status: "uploading", progress: 0, error: undefined });
      try {
        const r = await uploadDemo(it.file, videos, tagIds, (progress) => patch(it.id, { progress }));
        for (const v of r.videos?.filter((v) => v.error) ?? []) warnings.add(`${KIND[v.kind]}: ${v.error}`);
        patch(it.id, { status: "done", progress: 1 });
        r.duplicate ? known++ : ok++;
      } catch (e) {
        patch(it.id, { status: "error", error: (e as Error).message });
      }
      (qc.invalidateQueries({ queryKey: ["demos"] }), qc.invalidateQueries({ queryKey: ["feed"] }));
    }
    setBusy(false);
    if (ok) toast.success(`${ok} ${plural(ok, "демка загружена", "демки загружены", "демок загружено")} — статистика появится через минуту`);
    if (known) toast.success(`${known} ${plural(known, "демка уже была", "демки уже были", "демок уже было")} на сайте — ${plural(known, "матч добавлен", "матчи добавлены", "матчи добавлены")} к вам`);
    for (const w of warnings) toast.warning(w);
    // Uploaded files leave the list; failed ones stay for a retry.
    setItems((all) => all.filter((it) => it.status !== "done"));
  };

  const can = (k: VideoKind) => ent.data?.[`video.${k}`].allowed;
  const toggle = (k: VideoKind) => setVideos((v) => (v.includes(k) ? v.filter((x) => x !== k) : [...v, k]));
  const pickRef = useRef<(list?: FileList | null) => void>(() => {});
  const pick = (list?: FileList | null) => {
    const files = [...(list ?? [])];
    const bad = files.filter((f) => !DEMO_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext)));
    if (bad.length) toast.error(`Не демка: ${bad.map((f) => f.name).join(", ")}. Нужны ${DEMO_EXTENSIONS.join(", ")}`);
    setItems((all) => [
      ...all,
      ...files
        .filter((f) => !bad.includes(f) && !all.some((it) => it.file.name === f.name && it.file.size === f.size))
        .map((file) => ({ id: `${file.name}:${file.size}:${file.lastModified}`, file, status: "waiting" as const, progress: 0 })),
    ]);
  };

  pickRef.current = pick;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="font-display">Загрузить демки</CardTitle>
        <CardDescription>Можно несколько сразу — или просто перетащите файлы на страницу.</CardDescription>
      </CardHeader>
      {pageDrag && (
        <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center bg-background/80 backdrop-blur-sm animate-in-up">
          <div className="grid place-items-center gap-3 rounded-2xl border-2 border-dashed border-primary bg-primary/10 px-16 py-12 text-center">
            <Upload className="size-10 text-primary" />
            <div className="font-display text-xl font-bold">Отпустите, чтобы загрузить</div>
            <div className="text-sm text-muted-foreground">.dem, .dem.gz, .dem.bz2, .dem.zst</div>
          </div>
        </div>
      )}
      <CardContent className="space-y-4">
        <button
          type="button"
          onClick={() => input.current?.click()}
          onDragOver={(e) => (e.preventDefault(), setDrag(true))}
          onDragLeave={() => setDrag(false)}
          onDrop={() => setDrag(false) /* the page-wide handler takes the files */}
          className={cn(
            "flex w-full min-w-0 flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 text-center transition-colors",
            items.length ? "py-3" : "py-6",
            drag ? "border-primary bg-primary/10" : "hover:border-primary/60 hover:bg-secondary/40",
          )}
        >
          <Upload className="size-7 text-primary" />
          <span className="font-medium">{items.length ? "Добавить ещё" : "Перетащите .dem сюда или нажмите"}</span>
          {!items.length && <span className="text-xs text-muted-foreground">.dem, .dem.gz, .dem.bz2, .dem.zst · можно выбрать несколько</span>}
        </button>
        {/* No `accept`: .dem has no MIME type, so phone and some desktop pickers would hide it. */}
        <input ref={input} type="file" multiple hidden onChange={(e) => (pick(e.target.files), (e.target.value = ""))} />

        {items.length > 0 && (
          <ul className="divide-y rounded-lg border">
            {items.map((it) => (
              <li key={it.id} className="flex min-w-0 items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="truncate text-sm font-medium" title={it.file.name}>{it.file.name}</div>
                  {it.status === "uploading" ? (
                    <Progress value={it.progress * 100} className="h-1.5" />
                  ) : (
                    <div className={cn("num truncate text-xs", it.status === "error" ? "text-loss" : "text-muted-foreground")}>
                      {it.status === "error" ? it.error : it.status === "done" ? "загружена" : bytes(it.file.size)}
                    </div>
                  )}
                </div>
                {it.status === "uploading" ? (
                  <span className="num w-10 text-right text-xs text-muted-foreground">{Math.round(it.progress * 100)}%</span>
                ) : it.status === "done" ? (
                  <Check className="size-4 text-win" />
                ) : (
                  <Button size="icon" variant="ghost" className="size-8" disabled={busy} aria-label="Убрать"
                    onClick={() => setItems((all) => all.filter((x) => x.id !== it.id))}>
                    <X className="size-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {items.length > 0 && (
        <div className="space-y-4 animate-in-up">
        <div className="space-y-2">
          <div>
            <Label>Видео моей игры</Label>
            <p className="mt-1 text-xs text-muted-foreground">Можно выбрать несколько — закажутся для каждой демки.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {VIDEO_OPTIONS.map(({ kind, icon: Icon }) => {
              const on = videos.includes(kind);
              return (
                <Button key={kind} type="button" size="sm" variant="outline" aria-pressed={on} disabled={!can(kind) || busy} onClick={() => toggle(kind)}
                  className={cn(on && "border-primary bg-primary/15 text-primary hover:bg-primary/20 hover:text-primary")}>
                  {on ? <Check className="size-4" /> : <Icon className="size-4" />} {KIND[kind]}
                </Button>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          <div>
            <Label>Теги</Label>
            <p className="mt-1 text-xs text-muted-foreground">Например «Праки» — потом в профиле можно смотреть статистику только по ним.</p>
          </div>
          <TagToggles value={tagIds} onChange={setTagIds} disabled={busy} />
        </div>
        <Button className="w-full sm:w-auto sm:min-w-40" disabled={!pending.length || busy} onClick={run}>
          {busy
            ? `Загрузка ${items.filter((it) => it.status === "done").length + 1} из ${items.length}…`
            : pending.length > 1 ? `Загрузить ${pending.length} ${plural(pending.length, "демку", "демки", "демок")}` : "Загрузить"}
        </Button>
        {ent.data && !VIDEO_OPTIONS.some((o) => can(o.kind)) && (
          <p className="text-xs text-muted-foreground">Видео доступно с VIP или по гранту.</p>
        )}
        </div>
        )}
      </CardContent>
    </Card>
  );
}

const AUTH_CODE_HELP = "https://help.steampowered.com/en/wizard/HelpWithGameIssue/?appid=730&issueid=128";

function SteamImportCard({ className }: { className?: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["steam-link"], queryFn: () => api<SteamLink>("/api/steam-link") });
  const [auth, setAuth] = useState("");
  const [share, setShare] = useState("");
  const [editCode, setEditCode] = useState(false);
  const link = useMutation({
    mutationFn: () => api<{ queuedNewer: number; warning: string | null }>("/api/steam-link", { method: "PUT", json: { authCode: auth, shareCode: share } }),
    onSuccess: (r) => {
      if (r.warning) toast.warning(`Сохранено, но: ${r.warning}`);
      else toast.success(`Автоимпорт включён, найдено новых матчей: ${r.queuedNewer}`);
      qc.invalidateQueries({ queryKey: ["steam-link"] });
      (qc.invalidateQueries({ queryKey: ["demos"] }), qc.invalidateQueries({ queryKey: ["feed"] }));
    },
    onError: (e) => toast.error(e.message),
  });
  const recode = useMutation({
    mutationFn: (shareCode: string) => api<{ queuedNewer: number; warning: string | null }>("/api/steam-link", { method: "PATCH", json: { shareCode } }),
    onSuccess: (r) => {
      toast.success(r.warning ?? `Код обновлён, найдено новых матчей: ${r.queuedNewer}`);
      setShare("");
      setEditCode(false);
      qc.invalidateQueries({ queryKey: ["steam-link"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
    onError: (e) => toast.error(e.message),
  });
  const unlink = useMutation({
    mutationFn: () => api("/api/steam-link", { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["steam-link"] }),
  });
  const l = q.data?.link;
  const bot = q.data?.botSteamId;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display">
          <Link2 className="size-5 text-primary" /> Автоимпорт из Steam
        </CardTitle>
        <CardDescription>Матчи Premier и соревновательного режима будут появляться сами.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {l?.enabled ? (
          <>
            <div><Badge variant="outline" className="border-win/40 text-win">включён</Badge></div>
            <p className="text-muted-foreground">
              Последняя проверка: {ago(l.last_checked_at)}
              {l.last_error && <span className="block text-loss">{l.last_error}</span>}
            </p>
            {bot && (
              <p className="text-muted-foreground">
                Чтобы матчи появлялись через пару минут после игры,{" "}
                <a className="text-primary underline-offset-4 hover:underline" href={`https://steamcommunity.com/profiles/${bot}`} target="_blank" rel="noreferrer">
                  добавьте нашего бота в друзья
                </a>.
              </p>
            )}
            {editCode ? (
              <div className="space-y-1.5">
                <Label htmlFor="share-new">Код последнего матча</Label>
                <Input id="share-new" placeholder="CSGO-xxxxx-xxxxx-xxxxx-xxxxx-xxxxx" value={share} onChange={(e) => setShare(e.target.value)} autoComplete="off" />
                <p className="text-xs text-muted-foreground">Импорт продолжится с этого матча — так можно подтянуть пропущенные игры.</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" disabled={!share || recode.isPending} onClick={() => recode.mutate(share)}>
                    {recode.isPending ? "Проверяем…" : "Сохранить"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => (setEditCode(false), setShare(""))}>Отмена</Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditCode(true)}>Изменить код матча</Button>
                <Button variant="ghost" size="sm" onClick={() => unlink.mutate()}>Отключить</Button>
              </div>
            )}
          </>
        ) : (
          <>
            {l && !l.enabled && <p className="text-loss">Импорт остановлен{l.last_error ? `: ${l.last_error.replace(/\.$/, "")}` : ""}. Введите коды заново.</p>}
            <p className="text-muted-foreground">
              Один раз: <a className="text-primary underline-offset-4 hover:underline" href={AUTH_CODE_HELP} target="_blank" rel="noreferrer">создайте код аутентификации</a>{" "}
              и скопируйте код последнего матча (CS2 → Смотреть → Ваши матчи → Поделиться).
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="auth">Код аутентификации</Label>
              <Input id="auth" placeholder="XXXX-XXXXX-XXXX" value={auth} onChange={(e) => setAuth(e.target.value)} autoComplete="off" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="share">Код последнего матча</Label>
              <Input id="share" placeholder="CSGO-xxxxx-xxxxx-xxxxx-xxxxx-xxxxx" value={share} onChange={(e) => setShare(e.target.value)} autoComplete="off" />
            </div>
            <Button className="w-full" variant="secondary" disabled={!auth || !share || link.isPending} onClick={() => link.mutate()}>
              {link.isPending ? "Проверяем…" : "Включить автоимпорт"}
            </Button>
          </>
        )}
        <p className="text-xs text-muted-foreground">Коды дают доступ только к истории матчей.</p>
      </CardContent>
    </Card>
  );
}

